import React, { useReducer, useEffect, useState } from "react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  TouchSensor,
  useSensor,
} from "@dnd-kit/core";
import { Box, Typography } from "@mui/material";

// Beam and rotating arms
import BaseBeam from "../../../assets/workload/Beam.svg";
import RotatingBeam from "../../../assets/workload/RotatingBeam.svg";
// Pan and weight as backgrounds
import PanImage from "../../../assets/workload/WeighingPan.svg";
import WeightImage from "../../../assets/workload/Weight.svg";

const initialState = {
  leftPan: [],
  rightPan: [],
  availableItems: [1, 2, 3, 4, 5],
};

function reducer(state, action) {
  switch (action.type) {
    case "ADD_WEIGHT":
      return {
        ...state,
        availableItems: state.availableItems.filter((id) => id !== action.itemId),
        [action.side]: [...state[action.side], action.itemId],
      };
    case "REMOVE_WEIGHT":
      return {
        ...state,
        availableItems: [...state.availableItems, action.itemId].sort((a, b) => a - b),
        [action.side]: state[action.side].filter((id) => id !== action.itemId),
      };
    default:
      return state;
  }
}

/** Draggable for each weight */
const DraggableWeight = ({ id, children }) => {
  const { setNodeRef, listeners, attributes, transform } = useDraggable({ id });
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : "",
    cursor: "grab",
  };
  return (
    <Box ref={setNodeRef} {...listeners} {...attributes} sx={style}>
      {children}
    </Box>
  );
};

/** A single Pan droppable area */
const PanBox = ({ id, children, top, left }) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: "absolute",
        top,
        left,
        width: 120,
        height: 130,
        backgroundImage: `url(${PanImage})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "bottom",
        pointerEvents: "auto",
        transition: "top 0.3s ease",
      }}
    >
      {children}
    </Box>
  );
};

export default function WorkloadSatisfaction({ onResponseChange }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Sensors
  const pointerSensor = useSensor(PointerSensor);
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 0, tolerance: 0 } });
  const [currentSensor, setCurrentSensor] = useState(pointerSensor);

  useEffect(() => {
    const handleTouchStart = () => setCurrentSensor(touchSensor);
    const handleMouseDown = () => setCurrentSensor(pointerSensor);
    window.addEventListener("touchstart", handleTouchStart);
    window.addEventListener("mousedown", handleMouseDown);
    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, [pointerSensor, touchSensor]);

  // Handle dropping a weight onto a pan
  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const itemId = parseInt(active.id, 10);
    if (state.availableItems.includes(itemId)) {
      dispatch({ type: "ADD_WEIGHT", side: over.id, itemId });
    }
  };

  // Remove weight from a pan
  const handleRemoveItem = (itemId, side) => {
    dispatch({ type: "REMOVE_WEIGHT", side, itemId });
  };

  // Rotation & offset logic
  const rotationAngle = calcRotationAngle(state.leftPan.length, state.rightPan.length);
  const [leftPanOffset, rightPanOffset] = calcPanOffsets(rotationAngle);

  // Whenever pans change, compute workload category and notify parent
  useEffect(() => {
    const leftWeightCount = state.leftPan.length;
    const rightWeightCount = state.rightPan.length;
    const workloadDifference = Math.abs(leftWeightCount - rightWeightCount);

    let workloadCategory;
    if (workloadDifference === 0) {
      workloadCategory = "Perfectly Balanced";
    } else if (workloadDifference === 1) {
      workloadCategory = "Slightly Imbalanced";
    } else if (workloadDifference === 2) {
      workloadCategory = "Moderately Imbalanced";
    } else if (workloadDifference >= 3) {
      workloadCategory = "Severely Imbalanced";
    }

    onResponseChange && onResponseChange("workloadSatisfaction", workloadCategory);
  }, [state.leftPan, state.rightPan, onResponseChange]);

  return (
    <DndContext sensors={[currentSensor]} onDragEnd={handleDragEnd}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "60vh",
          justifyContent: "center",
          padding: "2rem",
          backgroundColor: "#F9FAFC",
          borderRadius: "12px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            marginBottom: "1rem",
            color: "#333",
            textAlign: "center",
          }}
        >
          Workload Satisfaction
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1.25rem",
            color: "#666",
            marginBottom: "2rem",
            textAlign: "center",
          }}
        >
          Add the weight to indicate how you feel about your current workload
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // Column on small screens, row on larger screens
            alignItems: { xs: "center", md: "flex-end" }, // Center items horizontally on small screens
            justifyContent: { xs: "center", md: "flex-end" }, // Center items horizontally on small screens
            borderBottom:"4px solid #526172",
            px:"10px",
            gap: 4,
          }}
        >
          {/* LEFT COLUMN: Available Weights */}
          <Box
            sx={{
              minWidth: { xs: "auto", md: "180px" },
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {state.availableItems.map((id) => (
              <DraggableWeight key={id} id={`${id}`}>
                <Box
                  sx={{
                    width: 35,
                    height: 50,
                    backgroundImage: `url(${WeightImage})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    cursor: "pointer",
                  }}
                />
              </DraggableWeight>
            ))}
          </Box>

          {/* RIGHT COLUMN: The Beam + Pans */}
          <Box
            sx={{
              position: "relative",
              width: 400,
              height: 300,
              flexShrink: 0,
              alignSelf: { xs: "center", md: "flex-start" },
            }}
          >
            {/* The base beam as an <img> */}
            <Box
              component="img"
              src={BaseBeam}
              alt="Base Beam"
              sx={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "800px",
                height: "300px",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />

            {/* Rotating Beam */}
            <Box
              component="img"
              src={RotatingBeam}
              alt="Rotating Beam"
              sx={{
                position: "absolute",
                top: "50px",
                left: "50%",
                transform: `translateX(-50%) rotate(${rotationAngle}deg)`,
                transformOrigin: "center center",
                width: "250px",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />

            {/* Left Pan */}
            <PanBox
              id="leftPan"
              top={`${75 + leftPanOffset}px`}
              left="40px"
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: 45,
                  left: 8,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "4px",
                  width: "90%",
                }}
              >
                {state.leftPan.map((itemId) => (
                  <Box
                    key={itemId}
                    onClick={() => handleRemoveItem(itemId, "leftPan")}
                    sx={{
                      width: 20,
                      height: 25,
                      backgroundImage: `url(${WeightImage})`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: -20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                }}
              >
                Work
              </Box>
            </PanBox>

            {/* Right Pan */}
            <PanBox
              id="rightPan"
              top={`${75 + rightPanOffset}px`}
              left="240px"
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: 45,
                  left: 8,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "4px",
                  width: "90%",
                }}
              >
                {state.rightPan.map((itemId) => (
                  <Box
                    key={itemId}
                    onClick={() => handleRemoveItem(itemId, "rightPan")}
                    sx={{
                      width: 20,
                      height: 25,
                      backgroundImage: `url(${WeightImage})`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: -40,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                Life (Personal)
              </Box>
            </PanBox>
          </Box>
        </Box>
      </Box>
    </DndContext>
  );
}

/** Rotation angle logic */
function calcRotationAngle(leftCount, rightCount) {
  const rotationPerWeight = 5;
  return (rightCount - leftCount) * rotationPerWeight;
}

/** Up/down offset for the pans */
function calcPanOffsets(angle) {
  const maxOffset = 40;
  const fraction = angle / 25;
  const offset = fraction * maxOffset;
  return [-offset, offset]; // left gets negative, right gets positive
}
